import type { Account } from "./types";
import type { OpenedTrade } from "../opened-trades/types";
import { VybeOrderType } from "../opened-trades/types";
import {
  calculateTradeAccountPnl,
  isMarketTrade,
  resolveProfitConversionRate,
} from "../opened-trades/utils";
import type { Quote } from "../subscribed-symbols/types";
import type { Symbol as VybeSymbol } from "../symbols/types";
import { VybeCalculationMode } from "../symbols/types";

type PriceQuote = Pick<Quote, "bid" | "ask"> & Partial<Quote>;
type SymbolLookup = Map<string, VybeSymbol>;
type QuoteLookup = Map<string, PriceQuote | { quote?: PriceQuote } | undefined>;

export interface DerivedAccountMetrics {
  profit: number;
  equity: number;
  margin: number;
  freeMargin: number;
  marginLevel: number;
}

export interface AccountMetricInputs {
  account: Account | null | undefined;
  trades: OpenedTrade[];
  symbols: SymbolLookup;
  quotes: QuoteLookup;
}

type MarginSide = "buy" | "sell";

interface SideAccumulator {
  volume: number;
  openPriceVolume: number;
  conversionVolume: number;
  marginRate: number;
}

interface SymbolMarginAccumulator {
  symbol: VybeSymbol;
  buy: SideAccumulator;
  sell: SideAccumulator;
}

const emptyMetrics: DerivedAccountMetrics = {
  profit: 0,
  equity: 0,
  margin: 0,
  freeMargin: 0,
  marginLevel: 0,
};

const makeSideAccumulator = (): SideAccumulator => ({
  volume: 0,
  openPriceVolume: 0,
  conversionVolume: 0,
  marginRate: 0,
});

const round = (value: number, digits = 8) => {
  const factor = 10 ** Math.max(0, Math.min(digits, 12));
  return Math.round(value * factor) / factor;
};

const isFinitePositive = (value: number | undefined | null): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

/**
 * Leverage used when the account reports none (0). The ViiSync engine treats 0 as 1:100
 * (ViiRiskEngine.DefaultLeverage); falling back to 1:1 instead is what showed Rohan (SR Global, 2026-09-25) the
 * full notional of 1 lot XAUUSD — 428,176 — as margin while the engine charged 4,282.
 */
export const DEFAULT_ACCOUNT_LEVERAGE = 100;

export const effectiveLeverage = (account: Pick<Account, "leverage">) =>
  isFinitePositive(account.leverage) ? account.leverage : DEFAULT_ACCOUNT_LEVERAGE;

const getQuote = (
  symbolName: string,
  quotes: QuoteLookup
): PriceQuote | undefined => {
  const entry = quotes.get(symbolName);
  if (!entry) return undefined;
  if ("bid" in entry && "ask" in entry) return entry;
  return entry.quote;
};

const getCloseQuote = (
  trade: OpenedTrade,
  quotes: QuoteLookup
) => getQuote(trade.symbol, quotes);

const getConversionRate = (
  fromCurrency: string | null | undefined,
  toCurrency: string | null | undefined,
  symbols: SymbolLookup,
  quotes: QuoteLookup
) =>
  resolveProfitConversionRate(fromCurrency, toCurrency, 1, symbols, quotes)
    ?.rate ?? 1;

const getMarginRate = (symbol: VybeSymbol, side: MarginSide) => {
  const index = side === "buy" ? 0 : 1;
  return symbol.mntnMarginRate?.[index] || symbol.initMarginRate?.[index] || 1;
};

const getMarginConversion = (
  trade: OpenedTrade,
  symbol: VybeSymbol,
  side: MarginSide,
  accountCurrency: string,
  symbols: SymbolLookup,
  quotes: QuoteLookup
) => {
  const dealRate = trade.dealInternalIn?.volumeRate;
  if (isFinitePositive(dealRate)) {
    return dealRate;
  }

  const marginCurrency = symbol.marginCurrency || accountCurrency;
  const rate = getConversionRate(
    marginCurrency,
    accountCurrency,
    symbols,
    quotes
  );

  if (rate !== 1) {
    return rate;
  }

  const quote = getCloseQuote(trade, quotes);
  if (
    quote &&
    symbol.currency.includes(accountCurrency) &&
    symbol.marginCurrency !== accountCurrency
  ) {
    return side === "buy" ? quote.ask : quote.bid;
  }

  return 1;
};

const addTradeToAccumulator = (
  accumulator: SymbolMarginAccumulator,
  trade: OpenedTrade,
  accountCurrency: string,
  symbols: SymbolLookup,
  quotes: QuoteLookup
) => {
  const side: MarginSide =
    trade.orderType === VybeOrderType.Sell ? "sell" : "buy";
  const sideAccumulator = side === "buy" ? accumulator.buy : accumulator.sell;
  const volume = trade.volume || Math.round((trade.lots || 0) * 100_000_000);
  const marginConversion = getMarginConversion(
    trade,
    accumulator.symbol,
    side,
    accountCurrency,
    symbols,
    quotes
  );

  sideAccumulator.volume += volume;
  sideAccumulator.openPriceVolume += volume * trade.openPrice;
  sideAccumulator.conversionVolume += volume * marginConversion;
  sideAccumulator.marginRate = getMarginRate(accumulator.symbol, side);
};

const averageOpenPrice = (side: SideAccumulator) =>
  side.volume > 0 ? side.openPriceVolume / side.volume : 0;

const averageConversionRate = (side: SideAccumulator) =>
  side.volume > 0 ? side.conversionVolume / side.volume : 1;

const lotsFromVolume = (volume: number) => round(volume * 1e-8, 8);

const contractLotsFromVolume = (volume: number, contractSize: number) =>
  round(lotsFromVolume(volume) * (contractSize || 1), 8);

const calculateBaseMargin = (
  symbol: VybeSymbol,
  account: Account,
  volume: number,
  useInitialMargin: boolean,
  useHedgedPrice: boolean,
  openPrice: number
) => {
  const initialMargin =
    !useInitialMargin && symbol.maintenanceMargin
      ? symbol.maintenanceMargin
      : symbol.initialMargin;
  const effectiveInitialMargin =
    useHedgedPrice && initialMargin ? symbol.hedgedMargin : initialMargin;
  const contractSize = symbol.contractSize || 1;
  const contractAmount = contractLotsFromVolume(
    volume,
    useHedgedPrice ? symbol.hedgedMargin : contractSize
  );
  const lotAmount = lotsFromVolume(volume);
  const leverage = effectiveLeverage(account);

  switch (symbol.calcMode) {
    case VybeCalculationMode.Forex:
      return effectiveInitialMargin > 0
        ? (lotAmount * effectiveInitialMargin) / leverage
        : contractAmount / leverage;
    case VybeCalculationMode.Futures:
    case VybeCalculationMode.ExchangeFutures:
    case VybeCalculationMode.FORTSFutures:
    case VybeCalculationMode.ExchangeMarginOption:
      return lotAmount * effectiveInitialMargin;
    case VybeCalculationMode.CFD:
    case VybeCalculationMode.ExchangeStocks:
      return effectiveInitialMargin > 0
        ? lotAmount * effectiveInitialMargin
        : contractAmount * openPrice;
    case VybeCalculationMode.CFDIndex:
      if (effectiveInitialMargin > 0) {
        return lotAmount * effectiveInitialMargin;
      }
      return symbol.tickSize
        ? (contractAmount * openPrice * (symbol.tickValue || 1)) /
            symbol.tickSize
        : 0;
    case VybeCalculationMode.CFDLeverage:
      return effectiveInitialMargin > 0
        ? (lotAmount * effectiveInitialMargin) / leverage
        : (contractAmount * openPrice) / leverage;
    case VybeCalculationMode.CalcMode5:
      return effectiveInitialMargin > 0
        ? lotAmount * effectiveInitialMargin
        : contractAmount;
    case VybeCalculationMode.ExchangeBounds:
      return effectiveInitialMargin > 0
        ? lotAmount * effectiveInitialMargin
        : round(
            contractAmount * openPrice * (symbol.faceValue || 1) * 0.01,
            symbol.marginPrecision || symbol.digits
          );
    default:
      return 0;
  }
};

const calculateDealMargin = (
  accumulator: SymbolMarginAccumulator,
  account: Account
) => {
  const { symbol, buy, sell } = accumulator;
  let margin = 0;

  if (buy.volume > sell.volume) {
    margin =
      calculateBaseMargin(
        symbol,
        account,
        buy.volume - sell.volume,
        false,
        false,
        averageOpenPrice(buy)
      ) *
      averageConversionRate(buy) *
      (buy.marginRate || 1);
  } else if (sell.volume > buy.volume) {
    margin =
      calculateBaseMargin(
        symbol,
        account,
        sell.volume - buy.volume,
        false,
        false,
        averageOpenPrice(sell)
      ) *
      averageConversionRate(sell) *
      (sell.marginRate || 1);
  }

  const hedgedVolume = Math.min(buy.volume, sell.volume);
  // MT5 SYMBOL_MARGIN_HEDGED = 0: the covered (hedged) volume is free. It used to fall through to a contract size
  // of 1 (`contractSize || 1`), so a fully hedged 1-lot XAUUSD pair showed ~4,282 instead of the engine's 0.
  if (hedgedVolume <= 0 || !isFinitePositive(symbol.hedgedMargin)) {
    return margin;
  }

  const totalVolume = buy.volume + sell.volume;
  const hedgedOpenPrice =
    totalVolume > 0
      ? (averageOpenPrice(buy) * buy.volume +
          averageOpenPrice(sell) * sell.volume) /
        totalVolume
      : 0;
  const hedgedConversion =
    totalVolume > 0
      ? (averageConversionRate(buy) * buy.volume +
          averageConversionRate(sell) * sell.volume) /
        totalVolume
      : 1;
  const hedgedRate = ((buy.marginRate || 1) + (sell.marginRate || 1)) * 0.5;

  return (
    margin +
    calculateBaseMargin(
      symbol,
      account,
      hedgedVolume,
      false,
      true,
      hedgedOpenPrice
    ) *
      hedgedConversion *
      hedgedRate
  );
};

export const calculateAccountProfit = ({
  account,
  trades,
  symbols,
  quotes,
}: AccountMetricInputs) => {
  const accountCurrency = account?.currency || "USD";

  return trades.reduce((sum, trade) => {
    if (!isMarketTrade(trade)) {
      return sum;
    }

    return (
      sum +
      calculateTradeAccountPnl({
        trade,
        symbol: symbols.get(trade.symbol),
        quote: getCloseQuote(trade, quotes),
        accountCurrency,
        symbols,
        quotes,
      })
    );
  }, 0);
};

export const calculateAccountMargin = ({
  account,
  trades,
  symbols,
  quotes,
}: AccountMetricInputs) => {
  if (!account) {
    return 0;
  }

  const bySymbol = new Map<string, SymbolMarginAccumulator>();

  for (const trade of trades) {
    if (!isMarketTrade(trade)) {
      continue;
    }

    const symbol = symbols.get(trade.symbol);
    if (!symbol) {
      continue;
    }

    let accumulator = bySymbol.get(trade.symbol);
    if (!accumulator) {
      accumulator = {
        symbol,
        buy: makeSideAccumulator(),
        sell: makeSideAccumulator(),
      };
      bySymbol.set(trade.symbol, accumulator);
    }

    addTradeToAccumulator(
      accumulator,
      trade,
      account.currency || "USD",
      symbols,
      quotes
    );
  }

  return Array.from(bySymbol.values()).reduce(
    (sum, accumulator) => sum + calculateDealMargin(accumulator, account),
    0
  );
};

export const deriveAccountMetrics = (
  inputs: AccountMetricInputs
): DerivedAccountMetrics => {
  const { account } = inputs;
  if (!account) {
    return emptyMetrics;
  }

  const profit = calculateAccountProfit(inputs);
  // The server's used margin is authoritative when the gateway says so (ViiSync: the engine's wallet, which applies
  // MT5 hedged margin, leverage tiers and calc modes exactly as it admits orders and runs stop-out). The client
  // formula below is then only a fallback for providers that send no margin (older gateways, the Vybe SDK gateway).
  const margin =
    account.marginFromServer === true && Number.isFinite(account.margin)
      ? account.margin
      : calculateAccountMargin(inputs);
  const equity = (account.balance || 0) + (account.credit || 0) + profit;
  const freeMargin = equity - margin;
  const marginLevel = margin > 0 ? (equity / margin) * 100 : 0;

  return {
    profit,
    equity,
    margin,
    freeMargin,
    marginLevel,
  };
};
